// deps
import React, { ReactElement, memo, useCallback, useContext, useEffect, useMemo, useRef } from "react";

// internal deps
import { Storage, captureError, isEmpty, useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";
import {
    CheckoutActionTypes,
    CheckoutContext,
    CheckoutRoute,
    ConfigContext,
    NEWRELIC_TABLES,
    recordNREvent,
    useNavigate,
} from "@swiggy-private/checkout-core";
import { PLACE_ORDER_SOURCE } from "@swiggy-private/payment-types";
import { PaymentMethod, PaymentMethodMeta, FullPageLoader } from "@swiggy-private/payment-ui";

// contexts
import { PlaceOrderContext } from "@payment-client/contexts/PlaceOrderContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";

// hooke and helpers
import { useOnPay } from "@payment-client/hooks/UseOnPay";
import {
    getTurboPaymentMethodFromPaymentResponse,
    isSdkTurboPlaceOrderTimeout,
} from "@payment-client/helpers/TurboPayment";
import { generatePaymentMethodId } from "@payment-client/helpers/PaymentHelper";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// styles
import styles from "./styles.scss";

// constants
import { PLACE_ORDER_DATA, SDK_TURBO_PLACE_ORDER_TIME } from "@payment-client/constants/StorageKeys";
import { isApiResponse } from "@payment-client/helpers/ApiHelper";

export const TURBO_PLACE_ORDER_DEBUG_SUB_TYPE = {
    SHOW_LOADER: "show_turbo_loader",
    PAY_CALL_MADE: "pay_call_made",
    PM_NOT_FOUND: "pm_not_found",
    PLACE_ORDER_CALL: "place_order_call",
};

function TurboPlaceOrder(props: RouteProps<{ selectedTurboMethod: string }>): ReactElement | null {
    const isMountedRef = useMountedRef();
    const { setCheckoutState } = useContext(CheckoutContext);
    const { isPlaceOrderHookMounted } = useContext(PlaceOrderContext);
    const configContext = useContext(ConfigContext);
    const { turboPayPlaceOrderData } = configContext;
    const { _rawPaymentResponse: paymentResponse, paymentsError } = useContext(ShowPaymentContext);
    const onPay = useOnPay();
    const navigateTo = useNavigate();

    const placeOrderTimeOutRef = useRef<boolean>(false);

    const paymentMethodObject = useRef<PaymentMethod<PaymentMethodMeta> | null>(null);

    const { data } = props;
    const selectedTurboMethod = decodeURIComponent(data?.selectedTurboMethod || "");

    const getTurboMethodObject = useCallback(() => {
        if (isEmpty(selectedTurboMethod)) {
            return null;
        }

        const turboGroup = getTurboPaymentMethodFromPaymentResponse(paymentResponse);

        if (isEmpty(turboGroup)) {
            return null;
        }

        return (
            turboGroup?.paymentMethods?.find(
                (pMethod: PaymentMethod<PaymentMethodMeta>) => generatePaymentMethodId(pMethod) === selectedTurboMethod,
            ) || null
        );
    }, [paymentResponse, selectedTurboMethod]);

    // TODO: remove after IM turbo loader debugging
    const orderData = useMemo(() => {
        const placeOrderData = Storage.getItem<{
            orderId?: string;
            orderPaymentMethod?: string;
            isTurboPaymentMethod?: boolean;
        }>(PLACE_ORDER_DATA, {});

        return {
            orderId: String(placeOrderData?.orderId),
            orderPaymentMethod: String(placeOrderData?.orderPaymentMethod),
            isTurboPaymentMethod: String(placeOrderData?.isTurboPaymentMethod),
        };
    }, []);

    useEffectOnce(() => {
        // closing the webview if turbo place order is timeout to avoid user stucking in forever loading screen
        if (isSdkTurboPlaceOrderTimeout()) {
            placeOrderTimeOutRef.current = true;

            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_SCREEN_LOADER_STATE,
                turboPlaceOrderTime: String(Storage.getItem(SDK_TURBO_PLACE_ORDER_TIME)),
                selectedTurboMethod,
                ...orderData,
            });
            // TODO: add this after turbo loader debug issue
            // closeWebview();
        }
    });

    useEffect(() => {
        if (!isMountedRef.current) {
            return;
        }
        if (paymentsError != null) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.TURBO_SDK_LOADER_SCREEN_ERROR,
                turboPlaceOrderTime: String(Storage.getItem(SDK_TURBO_PLACE_ORDER_TIME)),
                isPlaceOrderTimedout: String(placeOrderTimeOutRef.current),
                ...(isApiResponse(paymentsError) && {
                    errorMessage: paymentsError.statusMessage ?? "",
                    statusCode: paymentsError.statusCode ?? "",
                }),
                ...orderData,
                selectedTurboMethod,
            });
            setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
            navigateTo(CheckoutRoute.PAYMENTS, {}, true);
            return;
        }
        if (
            isEmpty(paymentResponse) ||
            !isEmpty(paymentMethodObject?.current) ||
            isEmpty(turboPayPlaceOrderData) ||
            !isPlaceOrderHookMounted
        ) {
            if (placeOrderTimeOutRef.current) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_LOADER_DEBUG,
                    subType: TURBO_PLACE_ORDER_DEBUG_SUB_TYPE.SHOW_LOADER,
                    turboPlaceOrderTime: String(Storage.getItem(SDK_TURBO_PLACE_ORDER_TIME)),
                    ...orderData,
                    selectedTurboMethod,
                    isPaymentResponseEmpty: String(isEmpty(paymentResponse)),
                    isPMObjectNonEmpty: String(!isEmpty(paymentMethodObject?.current)),
                    isTurboPlaceOrderDataEmpty: String(isEmpty(turboPayPlaceOrderData)),
                    isPlaceOrderHookMounted: String(isPlaceOrderHookMounted),
                });
            }
            return;
        }
        paymentMethodObject.current = getTurboMethodObject();

        if (!isEmpty(paymentMethodObject.current)) {
            if (placeOrderTimeOutRef.current) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_LOADER_DEBUG,
                    subType: TURBO_PLACE_ORDER_DEBUG_SUB_TYPE.PAY_CALL_MADE,
                    turboPlaceOrderTime: String(Storage.getItem(SDK_TURBO_PLACE_ORDER_TIME)),
                    ...orderData,
                    selectedTurboMethod,
                });
            }
            onPay(
                paymentMethodObject.current,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                { ...turboPayPlaceOrderData.extraParams, place_order_source: PLACE_ORDER_SOURCE.TURBOCHECKOUT },
                { ...turboPayPlaceOrderData.extraArbitaryData, isFromTurboPlaceOrder: true },
            );
        } else {
            if (placeOrderTimeOutRef.current) {
                recordNREvent({
                    eventType: NEWRELIC_TABLES.TURBO_LOADER_DEBUG,
                    subType: TURBO_PLACE_ORDER_DEBUG_SUB_TYPE.PM_NOT_FOUND,
                    turboPlaceOrderTime: String(Storage.getItem(SDK_TURBO_PLACE_ORDER_TIME)),
                    ...orderData,
                    selectedTurboMethod,
                });
            }
            captureError(new Error("Payment Method Not found for turbo: " + selectedTurboMethod));
            setCheckoutState({ type: CheckoutActionTypes.SHOW_PAYMENTS });
            // NOTE: incase SDK flows behave incorrectly see if this needs to have replace as false
            navigateTo(CheckoutRoute.PAYMENTS, {}, true);
        }
    }, [
        orderData,
        getTurboMethodObject,
        isMountedRef,
        isPlaceOrderHookMounted,
        navigateTo,
        onPay,
        paymentResponse,
        selectedTurboMethod,
        setCheckoutState,
        turboPayPlaceOrderData,
        paymentsError,
    ]);

    return (
        <div className={styles.container}>
            <FullPageLoader />
        </div>
    );
}

export default memo(TurboPlaceOrder);
